import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, Button, TextField,
  InputAdornment, Avatar, Alert, AlertTitle, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, Autocomplete, Snackbar,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Apple, Android, ScheduleSend, AccessTime, Delete, ArchiveOutlined,
  PhotoCameraOutlined, AddCircleOutline, EventAvailable, EventBusy,
  PlayArrow, History, NotificationsActive,
} from '@mui/icons-material';
import { format, formatDistanceToNowStrict } from 'date-fns';
import axios from 'axios';
import { useAuth } from './AuthContext';
// `API` (the trigger endpoint) used to be referenced here when the firing
// loop lived in this component; it now lives in `useScheduledDeployRunner`.
// Keeping axios since we still call /api/appdata/gitlab-tags below.
import {
  useScheduledDeploys,
  ScheduledDeploy,
  SCHEDULED_DEPLOY_FIRED_EVENT,
  ScheduledDeployFiredDetail,
} from '../utils/scheduledDeploys';
import { logAudit } from '../utils/audit';

interface Props { isActive: boolean }

type AppOption = {
  // Real D1 row id, or null when the feed hasn't assigned one yet. Never 0 —
  // collapsing missing ids to 0 made every id-less app look identical.
  appId: number | null;
  composerHost: string;
  composerAppId: string | number;
  platform: string;
  appName: string;
  sitekey: string;
  appVersion: string;
};

// Stable per-app-per-platform key for flag Sets and React keys. appId can be
// null/duplicated, so identity must come from host + platform.
const appKey = (a: { composerHost: string; platform: string }) => `${a.composerHost}:${a.platform}`;

// FIRE_TOLERANCE_MS used to live here when the firing loop ran in this
// page; the constant — and the loop — now live in the shared runner at
// `useScheduledDeployRunner` so the timer keeps ticking when operators
// navigate away from this page.

export const DeploySchedulerComponent: React.FC<Props> = ({ isActive }) => {
  const { appData, fetchDashboardData } = useAuth();
  const { list, add, update, remove } = useScheduledDeploys();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApps, setSelectedApps] = useState<AppOption[]>([]);
  const [aabFlags, setAabFlags] = useState<Set<string>>(new Set());
  const [screenshotFlags, setScreenshotFlags] = useState<Set<string>>(new Set());
  const [version, setVersion] = useState('');
  // Latest GitLab tag — same source the App Deployment page uses to build
  // its "DEPLOY VERSION X.Y.Z" button label. We fetch it once when the
  // page becomes active and use it as the default for every new schedule
  // so operators don't have to retype the version they already see in the
  // App Deployment header.
  const [latestTag, setLatestTag] = useState<string>('');
  const [isPatch, setIsPatch] = useState(false);
  const [scheduleAt, setScheduleAt] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d;
  });
  const [notes, setNotes] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({ open: false, msg: '', severity: 'success' });
  // Id of the schedule pending delete-confirmation (null = no dialog open).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Lazy-load apps list if needed
  useEffect(() => {
    if (isActive && (!appData || Object.keys(appData).length === 0)) {
      fetchDashboardData();
    }
    // The default schedule time is computed once at mount, so it goes stale
    // (and shows as a past value) if the page sits open. Re-seed it to a fresh
    // future time each time the page is opened.
    if (isActive) {
      const d = new Date();
      d.setHours(d.getHours() + 1, 0, 0, 0);
      setScheduleAt(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Fetch the latest GitLab tag on activation so we can prefill the version
  // field for every new schedule. Mirrors the App Deployment page exactly —
  // same endpoint, same "first tag" selection. Failure is non-fatal: the
  // version field just stays empty and the operator types it manually.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    (async () => {
      try {
        const tok = localStorage.getItem('token');
        const resp = await axios.get('/api/appdata/gitlab-tags', {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (cancelled) return;
        const tagName: string | undefined = resp.data?.tags?.[0]?.name;
        if (!tagName) return;
        setLatestTag(tagName);
        // Only auto-fill the version field if the operator hasn't already
        // typed something — we never want to clobber an in-progress edit.
        setVersion(prev => prev.trim() === '' ? tagName : prev);
      } catch (err) {
        console.warn('[scheduler] Failed to fetch latest GitLab tag (version prefill skipped):', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isActive]);

  const allApps = useMemo<AppOption[]>(() => {
    if (!appData) return [];
    return Object.values(appData as Record<string, any>).map((app: any) => ({
      appId: app.d1Id ?? null,
      composerHost: app.domain || 'Unknown',
      composerAppId: app.composer_app_id ?? '—',
      platform: (app.platform || '').toLowerCase(),
      appName: app.app_name || app.domain || 'Unknown',
      sitekey: app.sitekey || '',
      appVersion: app.android_version || app.ios_version || app.version || 'N/A',
    })).sort((a, b) => a.composerHost.localeCompare(b.composerHost));
  }, [appData]);

  // ------------------ In-page snackbar feedback ------------------
  // The actual firing loop now lives at the dashboard shell (see
  // useScheduledDeployRunner mounted in HomeComponent). That means a
  // schedule fires even when the operator is on App Deployment / Build
  // Cost / anywhere else in the app — no longer gated on this page being
  // active. We just listen for the runner's `fsam:scheduled-deploy-fired`
  // events here so operators who happen to BE on this page see a snackbar
  // alongside the live list update.
  useEffect(() => {
    if (!isActive) return;
    const onFired = (e: Event) => {
      const detail = (e as CustomEvent<ScheduledDeployFiredDetail>).detail;
      if (!detail) return;
      if (detail.outcome === 'completed') {
        setSnack({ open: true, msg: detail.message || 'Scheduled deploy fired.', severity: 'success' });
      } else if (detail.outcome === 'failed') {
        setSnack({ open: true, msg: detail.message || 'Scheduled deploy failed — see entry for details.', severity: 'error' });
      } else if (detail.outcome === 'cancelled') {
        setSnack({ open: true, msg: detail.message || 'Scheduled deploy auto-cancelled (overdue).', severity: 'info' });
      }
    };
    window.addEventListener(SCHEDULED_DEPLOY_FIRED_EVENT, onFired as EventListener);
    return () => window.removeEventListener(SCHEDULED_DEPLOY_FIRED_EVENT, onFired as EventListener);
  }, [isActive]);

  // ------------------ Form actions ------------------
  const resetForm = () => {
    setSelectedApps([]);
    setAabFlags(new Set());
    setScreenshotFlags(new Set());
    // Re-seed with the latest tag so the very next "New schedule" already
    // has the version filled in. Falls back to empty when we never managed
    // to fetch one.
    setVersion(latestTag);
    setIsPatch(false);
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    setScheduleAt(d);
    setNotes('');
  };

  const handleSave = () => {
    if (!scheduleAt) return;
    if (selectedApps.length === 0) {
      setSnack({ open: true, msg: 'Select at least one app.', severity: 'error' });
      return;
    }
    if (selectedApps.length > 18) {
      setSnack({ open: true, msg: 'Limit is 18 apps per schedule.', severity: 'error' });
      return;
    }
    if (!isPatch && !version.trim()) {
      setSnack({ open: true, msg: 'Version is required (or check Patch).', severity: 'error' });
      return;
    }
    if (scheduleAt.getTime() < Date.now() - 60_000) {
      setSnack({ open: true, msg: 'Pick a future time.', severity: 'error' });
      return;
    }
    const entry: ScheduledDeploy = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      createdAt: Date.now(),
      scheduledFor: scheduleAt.getTime(),
      isPatch,
      version: version.trim(),
      apps: selectedApps.map(a => ({
        ...a,
        generateAab: a.platform === 'android' && aabFlags.has(appKey(a)),
        uploadScreenshot: a.platform === 'ios' && screenshotFlags.has(appKey(a)),
      })),
      notes: notes.trim() || undefined,
      status: 'pending',
    };
    add(entry);
    setDialogOpen(false);
    resetForm();
    setSnack({ open: true, msg: `Scheduled for ${format(new Date(entry.scheduledFor), 'PPp')}.`, severity: 'success' });
    logAudit({
      action: 'schedule_create',
      targetType: 'schedule',
      targetId: entry.id,
      metadata: {
        scheduledFor: new Date(entry.scheduledFor).toISOString(),
        isPatch: entry.isPatch,
        version: entry.version,
        appCount: entry.apps.length,
        apps: entry.apps.map(a => ({ composerHost: a.composerHost, platform: a.platform, appId: a.appId })),
      },
    });
  };

  const fireNow = (sched: ScheduledDeploy) => {
    // Advance scheduledFor to now so the timer picks it up next tick.
    update(sched.id, { scheduledFor: Date.now() });
    setSnack({ open: true, msg: 'Will fire on next tick (within 15s).', severity: 'info' });
  };

  const pending = list.filter(s => s.status === 'pending').sort((a, b) => a.scheduledFor - b.scheduledFor);
  const history = list.filter(s => s.status !== 'pending').sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0)).slice(0, 30);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Deploy Scheduler</Typography>
            <Typography variant="body2" color="textSecondary">
              Schedule a deploy for a future time (e.g. off-hours). The dashboard must be open when the schedule fires.
            </Typography>
          </Box>
          <Button
            data-tour="new-schedule"
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={() => setDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            New schedule
          </Button>
        </Stack>

        <Alert severity="info" icon={<NotificationsActive />} sx={{ mb: 3, borderRadius: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>How scheduling works</AlertTitle>
          Each schedule fires when its scheduled time passes <strong>as long as the App Manager tab is open</strong> in your browser — you don't need to stay on this page, any page in the dashboard keeps the timer running. Late schedules (more than 5 minutes overdue) auto-cancel instead of firing surprise deploys, so closing the tab over a long weekend won't trigger a Monday-morning surprise.
          Enable browser notifications on the App Deployment page to get pinged when a scheduled deploy lands.
        </Alert>

        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <EventAvailable sx={{ color: '#6366f1' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Pending schedules</Typography>
            <Chip
              size="small"
              label={`${pending.length} pending`}
              sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.12)', color: '#4338ca' }}
            />
          </Stack>
          {pending.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">No pending schedules. Click <strong>New schedule</strong> to add one.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {pending.map(sched => <ScheduleCard key={sched.id} sched={sched} onCancel={() => setConfirmDeleteId(sched.id)} onFireNow={() => fireNow(sched)} />)}
            </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <History sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Recent activity</Typography>
          </Stack>
          {history.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No fired schedules yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {history.map(s => (
                <Box
                  key={s.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    alignItems: { sm: 'center' },
                  }}
                >
                  <Chip
                    size="small"
                    icon={s.status === 'completed' ? <EventAvailable sx={{ fontSize: 14 }} /> : <EventBusy sx={{ fontSize: 14 }} />}
                    label={s.status}
                    sx={{
                      fontWeight: 700,
                      bgcolor: s.status === 'completed' ? 'rgba(22, 163, 74, 0.12)'
                        : s.status === 'failed' ? 'rgba(220, 38, 38, 0.12)'
                        : 'rgba(148, 163, 184, 0.18)',
                      color: s.status === 'completed' ? '#15803d'
                        : s.status === 'failed' ? '#b91c1c'
                        : '#475569',
                      textTransform: 'capitalize',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.apps.length} app{s.apps.length !== 1 ? 's' : ''} · {s.isPatch ? 'patch' : `v${s.version}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled {format(new Date(s.scheduledFor), 'MMM dd, HH:mm')}
                      {s.completedAt ? ` · ${s.status} at ${format(new Date(s.completedAt), 'HH:mm')}` : ''}
                      {s.errorMessage ? ` · ${s.errorMessage}` : ''}
                    </Typography>
                  </Box>
                  <Tooltip title="Remove from history" arrow>
                    <IconButton size="small" onClick={() => setConfirmDeleteId(s.id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>

        {/* New schedule dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 800 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
                <ScheduleSend sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>New scheduled deploy</Typography>
                <Typography variant="caption" color="text.secondary">Pick apps, version, and when it should fire.</Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <Autocomplete
                multiple
                options={allApps}
                value={selectedApps}
                onChange={(_, v) => setSelectedApps(v)}
                getOptionLabel={(opt) => `${opt.composerHost} (${opt.platform})`}
                // Match on app name and site key too, not just the host shown in
                // the label (default Autocomplete filtering only sees the label).
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(o =>
                    o.composerHost.toLowerCase().includes(q) ||
                    o.appName.toLowerCase().includes(q) ||
                    o.sitekey.toLowerCase().includes(q),
                  );
                }}
                isOptionEqualToValue={(a, b) => appKey(a) === appKey(b)}
                renderOption={(props, opt) => (
                  <li {...props} key={appKey(opt)}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 22, height: 22, bgcolor: opt.platform === 'ios' ? '#0f172a' : '#3DDC84' }}>
                        {opt.platform === 'ios' ? <Apple sx={{ fontSize: 14 }} /> : <Android sx={{ fontSize: 14 }} />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.composerHost}</Typography>
                        <Typography variant="caption" color="text.secondary">v{opt.appVersion} · app #{opt.composerAppId}</Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, index) => (
                    <Chip
                      label={opt.composerHost}
                      size="small"
                      avatar={<Avatar sx={{ bgcolor: opt.platform === 'ios' ? '#0f172a' : '#3DDC84' }}>{opt.platform === 'ios' ? <Apple sx={{ fontSize: 12 }} /> : <Android sx={{ fontSize: 12 }} />}</Avatar>}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Apps to deploy" placeholder="Search apps" />
                )}
              />

              {selectedApps.length > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, maxHeight: 200, overflowY: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Per-app build flags
                  </Typography>
                  <Stack spacing={0.5}>
                    {selectedApps.map(a => (
                      <Stack key={appKey(a)} direction="row" spacing={1} alignItems="center">
                        {a.platform === 'ios' ? <Apple sx={{ fontSize: 14 }} /> : <Android sx={{ fontSize: 14, color: '#3DDC84' }} />}
                        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>{a.composerHost}</Typography>
                        {a.platform === 'android' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={aabFlags.has(appKey(a))}
                                onChange={(_, c) => setAabFlags(prev => { const s = new Set(prev); c ? s.add(appKey(a)) : s.delete(appKey(a)); return s; })}
                              />
                            }
                            label={<Typography variant="caption">AAB</Typography>}
                          />
                        )}
                        {a.platform === 'ios' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={screenshotFlags.has(appKey(a))}
                                onChange={(_, c) => setScreenshotFlags(prev => { const s = new Set(prev); c ? s.add(appKey(a)) : s.delete(appKey(a)); return s; })}
                              />
                            }
                            label={<Typography variant="caption">Screenshot</Typography>}
                          />
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Version"
                  placeholder="e.g. 1.2.3"
                  value={version}
                  onChange={e => setVersion(e.target.value)}
                  sx={{ flex: 1 }}
                  disabled={isPatch}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">v</InputAdornment>,
                    // "Use latest" shortcut — only shows when (a) we have a
                    // latest-tag value, (b) the user has edited it away from
                    // that value, and (c) the patch checkbox isn't shadowing
                    // the field. Restores the auto-fill in one click.
                    endAdornment: latestTag && version !== latestTag && !isPatch ? (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setVersion(latestTag)}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, px: 1 }}
                        >
                          Use latest ({latestTag})
                        </Button>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  helperText={
                    isPatch
                      ? 'Patch mode — version auto-increments per app at firing time.'
                      : latestTag
                        ? `Auto-filled from the latest GitLab tag (${latestTag}). Edit if you need a different version.`
                        : 'Enter the target version, e.g. 1.2.3'
                  }
                />
                <FormControlLabel
                  control={<Checkbox checked={isPatch} onChange={(_, c) => setIsPatch(c)} />}
                  label="Patch (auto-increment)"
                />
              </Stack>

              <DateTimePicker
                label="Schedule for"
                value={scheduleAt}
                onChange={(v) => setScheduleAt(v)}
                disablePast
                slotProps={{ textField: { fullWidth: true } }}
              />

              <TextField
                label="Notes (optional)"
                placeholder="Why this is scheduled, ticket links, etc."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                multiline
                minRows={2}
              />

              {scheduleAt && (
                <Typography variant="caption" color="text.secondary">
                  Will fire {formatDistanceToNowStrict(scheduleAt, { addSuffix: true })} ({format(scheduleAt, 'PPp')})
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={handleSave} startIcon={<ScheduleSend />}>
              Schedule deploy
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.msg}</Alert>
        </Snackbar>

        <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}>
          <DialogTitle>Delete scheduled deploy?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This removes the schedule and it will no longer fire. This can't be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => { if (confirmDeleteId) remove(confirmDeleteId); setConfirmDeleteId(null); }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

const ScheduleCard: React.FC<{ sched: ScheduledDeploy; onCancel: () => void; onFireNow: () => void }> = ({ sched, onCancel, onFireNow }) => {
  const ms = sched.scheduledFor - Date.now();
  const dueLabel = ms <= 0
    ? `overdue ${Math.round(-ms / 1000)}s`
    : `in ${formatDistanceToNowStrict(new Date(sched.scheduledFor))}`;
  const aabCount = sched.apps.filter(a => a.generateAab).length;
  const screenshotCount = sched.apps.filter(a => a.uploadScreenshot).length;
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: ms <= 0 ? 'rgba(245, 158, 11, 0.5)' : 'divider',
        backgroundColor: ms <= 0 ? 'rgba(245, 158, 11, 0.06)' : 'transparent',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {sched.apps.length} app{sched.apps.length !== 1 ? 's' : ''} · {sched.isPatch ? 'patch' : `v${sched.version}`}
            </Typography>
            <Chip
              size="small"
              icon={<AccessTime sx={{ fontSize: 14 }} />}
              label={dueLabel}
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: ms <= 0 ? 'rgba(245, 158, 11, 0.18)' : 'rgba(99, 102, 241, 0.12)', color: ms <= 0 ? '#92400e' : '#4338ca' }}
            />
            {aabCount > 0 && (
              <Chip size="small" icon={<ArchiveOutlined sx={{ fontSize: 12 }} />} label={`AAB ${aabCount}`} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#15803d' }} />
            )}
            {screenshotCount > 0 && (
              <Chip size="small" icon={<PhotoCameraOutlined sx={{ fontSize: 12 }} />} label={`SS ${screenshotCount}`} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#b45309' }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Fires {format(new Date(sched.scheduledFor), 'PPp')} · created {format(new Date(sched.createdAt), 'MMM dd, HH:mm')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.5 }}>
            {sched.apps.slice(0, 6).map(a => (
              <Chip key={appKey(a)} size="small" label={a.composerHost} sx={{ height: 18, fontSize: '0.65rem' }} />
            ))}
            {sched.apps.length > 6 && (
              <Typography variant="caption" color="text.secondary">+{sched.apps.length - 6} more</Typography>
            )}
          </Stack>
          {sched.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic' }}>
              "{sched.notes}"
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Fire now" arrow>
            <IconButton size="small" onClick={onFireNow} sx={{ color: '#6366f1' }}>
              <PlayArrow fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel schedule" arrow>
            <IconButton size="small" onClick={onCancel} sx={{ color: 'error.main' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DeploySchedulerComponent;
